import React, { useState, useEffect } from 'react';
import axios from 'axios';

export default function InvoiceView({ id, invoice, onClose, onUpdate, locations }) {
  const [editedData, setEditedData] = useState(invoice);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Initialiser les données éditées avec les données de l'invoice
  useEffect(() => {
    setEditedData(invoice);
  }, [invoice]);

  const handleInputChange = (field, value) => {
    setEditedData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveEdit = async () => {
    try {
      setLoading(true);
      setError(null);

      // Créer un objet avec seulement les données nécessaires pour l'API
      const updateData = {
        supplierName: editedData.supplierName || '',
        invoiceNumber: editedData.invoiceNumber || '',
        date: editedData.date ? new Date(editedData.date).toISOString() : new Date().toISOString(),
        subtotalHT: parseFloat(editedData.subtotalHT) || 0,
        totalTTC: parseFloat(editedData.totalTTC) || 0,
        taxRate: parseFloat(editedData.taxRate) || 0,
        currency: editedData.currency || 'TND',
        location: editedData.location || null
      };

      console.log('Données envoyées pour confirmation:', updateData);

      const response = await axios.post(`/api/invoices/${id}/confirm`, updateData);
      
      if (response.data.success) {
        setSuccess(true);
        setTimeout(() => {
          onUpdate();
        }, 1500);
      } else {
        throw new Error(response.data.message || 'Erreur lors de la confirmation');
      }
    } catch (err) {
      console.error('Erreur sauvegarde:', err);
      setError(err.response?.data?.message || err.message || 'Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setEditedData(invoice);
    onClose();
  };

  const formatCurrency = (amount, currency = 'TND') => {
    if (amount == null || amount === 0 || isNaN(amount)) return '-';
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3
    }).format(amount) + ` ${currency}`;
  };

  return (
    <div className="invoice-view">
      {/* Section d'information */}
      <div className="invoice-info-grid">
        <div className="info-section">
          <h4>Informations fournisseur</h4>
          <div className="form-group">
            <label>Nom du fournisseur</label>
            <input
              type="text"
              value={editedData.supplierName || ''}
              onChange={(e) => handleInputChange('supplierName', e.target.value)}
              className="form-input"
              placeholder="Nom du fournisseur"
            />
          </div>
          
          <div className="form-group">
            <label>Numéro de facture</label>
            <input
              type="text"
              value={editedData.invoiceNumber || ''}
              onChange={(e) => handleInputChange('invoiceNumber', e.target.value)}
              className="form-input"
              placeholder="N° facture"
            />
          </div>
        </div>

        <div className="info-section">
          <h4>Informations financières</h4>
          <div className="form-row">
            <div className="form-group">
              <label>Montant HT</label>
              <input
                type="number"
                step="0.001"
                value={editedData.subtotalHT || ''}
                onChange={(e) => handleInputChange('subtotalHT', e.target.value)}
                className="form-input"
                placeholder="0.000"
              />
            </div>
            
            <div className="form-group">
              <label>TVA (%)</label>
              <input
                type="number"
                step="0.1"
                value={editedData.taxRate || ''}
                onChange={(e) => handleInputChange('taxRate', e.target.value)}
                className="form-input"
                placeholder="19"
              />
            </div>
            
            <div className="form-group">
              <label>Montant TTC</label>
              <input
                type="number"
                step="0.001"
                value={editedData.totalTTC || ''}
                onChange={(e) => handleInputChange('totalTTC', e.target.value)}
                className="form-input"
                placeholder="0.000"
              />
            </div>
          </div>
          
          <div className="form-group">
            <label>Devise</label>
            <select
              value={editedData.currency || 'TND'}
              onChange={(e) => handleInputChange('currency', e.target.value)}
              className="form-select"
            >
              <option value="TND">TND (Dinar tunisien)</option>
              <option value="EUR">EUR (Euro)</option>
              <option value="USD">USD (Dollar US)</option>
            </select>
          </div>
        </div>

        <div className="info-section">
          <h4>Date</h4>
          <div className="form-group">
            <label>Date de la facture</label>
            <input
              type="date"
              value={editedData.date ? new Date(editedData.date).toISOString().split('T')[0] : ''}
              onChange={(e) => handleInputChange('date', e.target.value)}
              className="form-input"
            />
          </div>
        </div>

        {/* Section localisation - IMPORTANTE */}
        <div className="info-section location-section">
          <h4>📍 Affectation à une localisation</h4>
          <div className="form-group">
            <label>Sélectionner la localisation</label>
            <select
              value={editedData.location || ''}
              onChange={(e) => handleInputChange('location', e.target.value)}
              className="form-select location-select"
              style={{ 
                borderColor: editedData.location ? '#4299e1' : '#e2e8f0',
                backgroundColor: editedData.location ? '#ebf8ff' : '#f7fafc'
              }}
            >
              <option value="">-- Sélectionner une localisation --</option>
              {locations && locations.map(location => (
                <option key={location._id} value={location._id}>
                  {location.name}
                </option>
              ))}
            </select>
            <small className="form-help">
              Cette facture sera comptabilisée comme une charge pour la localisation sélectionnée.
              {!editedData.location && (
                <span style={{ color: '#e53e3e', fontWeight: 'bold' }}>
                  ⚠️ La localisation est obligatoire pour confirmer la facture.
                </span>
              )}
            </small>
          </div>
          
          {editedData.location && (
            <div className="location-info">
              <span className="location-badge">
                📍 {locations.find(l => l._id === editedData.location)?.name || 'Localisation sélectionnée'}
              </span>
              <p className="location-description">
                La facture sera affectée à cette localisation et prise en compte dans les statistiques.
              </p>
            </div>
          )}
        </div>

        {/* Métadonnées */}
        <div className="info-section metadata">
          <h4>Métadonnées</h4>
          <div className="metadata-grid">
            <div className="metadata-item">
              <span className="metadata-label">Méthode d'extraction:</span>
              <span className="metadata-value">
                {invoice.ocrEngine === 'mistral' ? '🤖 Mistral AI' : '📄 OCR Standard'}
              </span>
            </div>
            
            <div className="metadata-item">
              <span className="metadata-label">Statut:</span>
              <span className={`metadata-value status-${invoice.status || 'pending'}`}>
                {invoice.status === 'confirmed' ? '✅ Confirmée' : '⏳ En attente'}
              </span>
            </div>
            
            <div className="metadata-item">
              <span className="metadata-label">Date d'import:</span>
              <span className="metadata-value">
                {new Date(invoice.createdAt).toLocaleDateString('fr-FR')}
              </span>
            </div>
            
            {invoice.confidenceScore && (
              <div className="metadata-item">
                <span className="metadata-label">Fiabilité:</span>
                <span className="metadata-value">
                  <div className="confidence-bar">
                    <div 
                      className="confidence-fill"
                      style={{ width: `${invoice.confidenceScore}%` }}
                    ></div>
                    <span className="confidence-text">{invoice.confidenceScore}%</span>
                  </div>
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Messages d'erreur/succès */}
      {error && (
        <div className="alert alert-error">
          <strong>❌ Erreur:</strong> {error}
        </div>
      )}
      
      {success && (
        <div className="alert alert-success">
          <strong>✅ Succès!</strong> Facture confirmée et affectée à la localisation.
        </div>
      )}

      {/* Boutons d'action */}
      <div className="invoice-actions">
        <div className="actions-left">
          <button
            onClick={handleCancel}
            className="btn btn-secondary"
            disabled={loading}
          >
            Annuler
          </button>
        </div>
        
        <div className="actions-right">
          <button
            onClick={handleSaveEdit}
            disabled={loading || !editedData.location}
            className={`btn btn-primary ${!editedData.location ? 'btn-disabled' : ''}`}
            title={!editedData.location ? 'Veuillez sélectionner une localisation' : ''}
          >
            {loading ? (
              <>
                <span className="spinner"></span>
                Enregistrement...
              </>
            ) : (
              <>
                <span>✅</span>
                Confirmer la facture
              </>
            )}
          </button>
        </div>
      </div>

      <style jsx>{`
        .invoice-view {
          max-width: 100%;
        }
        
        .invoice-info-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 1.5rem;
          margin-bottom: 2rem;
        }
        
        .info-section {
          background: #f8fafc;
          padding: 1.5rem;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
        }
        
        .info-section h4 {
          margin-top: 0;
          margin-bottom: 1.2rem;
          color: #2d3748;
          font-size: 1.1rem;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        
        .location-section {
          border: 2px dashed #4299e1;
          background: #ebf8ff;
        }
        
        .form-group {
          margin-bottom: 1.2rem;
        }
        
        .form-group label {
          display: block;
          margin-bottom: 0.5rem;
          font-weight: 500;
          color: #4a5568;
          font-size: 0.9rem;
        }
        
        .form-input,
        .form-select {
          width: 100%;
          padding: 0.625rem 0.75rem;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          font-size: 1rem;
          transition: all 0.2s ease;
          background: white;
        }
        
        .form-input:focus,
        .form-select:focus {
          outline: none;
          border-color: #4299e1;
          box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
        }
        
        .location-select {
          font-size: 1rem;
          padding: 0.75rem;
        }
        
        .form-help {
          display: block;
          margin-top: 0.5rem;
          font-size: 0.85rem;
          color: #718096;
        }
        
        .form-row {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1rem;
        }
        
        .location-info {
          margin-top: 1rem;
          padding: 1rem;
          background: white;
          border-radius: 6px;
          border: 1px solid #cbd5e0;
        }
        
        .location-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          background: #4299e1;
          color: white;
          border-radius: 20px;
          font-weight: 600;
          font-size: 0.9rem;
        }
        
        .location-description {
          margin: 0.75rem 0 0;
          font-size: 0.9rem;
          color: #4a5568;
        }
        
        .metadata {
          grid-column: 1 / -1;
        }
        
        .metadata-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem;
        }
        
        .metadata-item {
          display: flex;
          flex-direction: column;
        }
        
        .metadata-label {
          font-size: 0.85rem;
          color: #718096;
          margin-bottom: 0.25rem;
        }
        
        .metadata-value {
          font-weight: 500;
          color: #2d3748;
        }
        
        .status-confirmed {
          color: #38a169;
        }
        
        .status-pending {
          color: #d69e2e;
        }
        
        .confidence-bar {
          position: relative;
          height: 24px;
          background: #e2e8f0;
          border-radius: 12px;
          overflow: hidden;
        }
        
        .confidence-fill {
          position: absolute;
          top: 0;
          left: 0;
          height: 100%;
          background: linear-gradient(90deg, #48bb78, #68d391);
          transition: width 0.3s ease;
        }
        
        .confidence-text {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          font-weight: 600;
          color: white;
          text-shadow: 0 1px 2px rgba(0,0,0,0.2);
        }
        
        .invoice-actions {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-top: 1.5rem;
          border-top: 1px solid #e2e8f0;
          margin-top: 2rem;
        }
        
        .btn {
          padding: 0.75rem 1.5rem;
          border: none;
          border-radius: 8px;
          font-weight: 600;
          font-size: 1rem;
          cursor: pointer;
          transition: all 0.2s ease;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
        }
        
        .btn-primary {
          background: #4299e1;
          color: white;
        }
        
        .btn-primary:hover:not(:disabled) {
          background: #3182ce;
          transform: translateY(-1px);
        }
        
        .btn-primary:disabled {
          background: #90cdf4;
          cursor: not-allowed;
          opacity: 0.6;
        }
        
        .btn-secondary {
          background: #e2e8f0;
          color: #4a5568;
        }
        
        .btn-secondary:hover {
          background: #cbd5e0;
        }
        
        .btn-disabled {
          position: relative;
        }
        
        .btn-disabled:before {
          content: attr(title);
          position: absolute;
          bottom: 100%;
          left: 50%;
          transform: translateX(-50%);
          padding: 0.5rem;
          background: #2d3748;
          color: white;
          border-radius: 4px;
          font-size: 0.85rem;
          white-space: nowrap;
          margin-bottom: 0.5rem;
          opacity: 0;
          visibility: hidden;
          transition: all 0.2s ease;
        }
        
        .btn-disabled:hover:before {
          opacity: 1;
          visibility: visible;
        }
        
        .alert {
          padding: 1rem;
          border-radius: 6px;
          margin-bottom: 1.5rem;
          border-left: 4px solid transparent;
        }
        
        .alert-error {
          background: #fff5f5;
          border-left-color: #f56565;
          color: #c53030;
        }
        
        .alert-success {
          background: #f0fff4;
          border-left-color: #48bb78;
          color: #276749;
        }
        
        .spinner {
          display: inline-block;
          width: 16px;
          height: 16px;
          border: 2px solid transparent;
          border-top: 2px solid white;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @media (max-width: 768px) {
          .form-row {
            grid-template-columns: 1fr;
          }
          
          .invoice-actions {
            flex-direction: column;
            gap: 1rem;
          }
          
          .actions-left,
          .actions-right {
            width: 100%;
          }
          
          .btn {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
}